import type { ECharts } from 'echarts';

type EChartsModule = typeof import('echarts');

/**
 * @name loadEcharts
 * @desc Carga diferida de echarts para ngx-echarts (NgxEchartsModule.forRoot) con un init() que:
 * - Reutiliza la instancia si el elemento ya tiene una. Si las options cambian mientras se descarga
 *   echarts, ngx-echarts vuelve a llamar init() sobre el mismo elemento ("already initialized on the dom").
 * - Espera a que el elemento tenga tamaño. Una grafica dentro de un contenedor oculto (pestaña no
 *   activa, seccion colapsada) mide 0 y echarts avisa "Can't get DOM width or height".
 * - Usa el tema 'v5' por defecto (aspecto de echarts 5).
 * ngx-echarts espera (await) el resultado de init(), por lo que puede ser una promesa.
 */
export const loadEcharts = () =>
    // echarts 6 cambio la paleta y la posicion por defecto de la leyenda; el tema 'v5' (que se
    // registra al importarlo) conserva el aspecto de las graficas y se usa cuando no se indica otro
    Promise.all([import('echarts'), import('echarts/theme/v5.js')]).then(([echarts]) => {
        // Inicializaciones en espera por elemento, para no crear dos instancias sobre el mismo
        const pending = new WeakMap<HTMLElement, Promise<ECharts>>();

        const init = (dom: HTMLElement, theme: Parameters<EChartsModule['init']>[1] = 'v5', opts?: Parameters<EChartsModule['init']>[2]) => {
            const existing = echarts.getInstanceByDom(dom);
            if (existing) {
                return existing;
            }
            if (dom.clientWidth > 0 && dom.clientHeight > 0) {
                return echarts.init(dom, theme, opts);
            }
            if (!pending.has(dom)) {
                pending.set(
                    dom,
                    new Promise<ECharts>((resolve) => {
                        const observer = new ResizeObserver(() => {
                            if (dom.clientWidth > 0 && dom.clientHeight > 0) {
                                observer.disconnect();
                                pending.delete(dom);
                                resolve(echarts.getInstanceByDom(dom) ?? echarts.init(dom, theme, opts));
                            }
                        });
                        observer.observe(dom);
                    }),
                );
            }
            return pending.get(dom);
        };

        return { ...echarts, init: init as unknown as EChartsModule['init'] };
    });
